import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Send, ExternalLink, XCircle, Loader2, RefreshCw, PlusCircle, Trash2, Clock } from "lucide-react";
import { LetterheadOptions } from "./LetterheadOptions";
import { RailSelector } from "./payments/RailSelector";

/**
 * Admin · Buyer Quotations panel + Draft drawer.
 *
 * Two exports:
 *   • BuyerQuotationDraftDrawer — inline drawer rendered under a Quote
 *     Request in the QuoteRequests tab.  Lets the admin edit line items /
 *     rates and hit Send Quote.
 *   • AdminBuyerQuotations — full "Buyer Quotations" tab with kanban lanes
 *     (Draft / Sent / Accepted / Expired / Revision Requested / Cancelled).
 *
 * The two share a common `BuyerQuotationEditor` internal form so the flow
 * feels identical whether the admin creates a draft from a QR or edits an
 * existing draft from the tab.
 */

const STATUS_COLOUR = {
  draft: "#B87333",
  sent: "#D8B23E",
  accepted: "#2FB999",
  expired: "#8A8A82",
  cancelled: "#FF5A5F",
  revision_requested: "#B94FEB",
};

function StatusPill({ status }) {
  const colour = STATUS_COLOUR[status] || "#8A8A82";
  const label = (status || "").replace("_", " ").toUpperCase();
  return (
    <span data-testid={`bq-status-${status}`}
          className="font-grotesk text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-sm border"
          style={{ color: colour, borderColor: colour + "60", background: colour + "12" }}>
      {label}
    </span>
  );
}

function BuyerQuotationEditor({ api, initial, onSaved, onSent }) {
  const [items, setItems] = useState(initial?.items?.length
    ? initial.items.map((i) => ({ ...i }))
    : [{ name: "", brand: "", unit: "MT", hsn: "", category: "", quantity: 1, unit_price: 0 }]);
  const [ship, setShip] = useState({
    shipping_name: initial?.shipping_name || "",
    shipping_phone: initial?.shipping_phone || "",
    shipping_address: initial?.shipping_address || "",
    shipping_state: initial?.shipping_state || "",
    gst_number: initial?.gst_number || "",
    project_name: initial?.project_name || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [rail, setRail] = useState(initial?.payment_rail || "razorpay");
  // Live totals preview driven by admin-controlled Financial Rules.
  const [preview, setPreview] = useState({
    subtotal: 0, discount_rate: initial?.discount_rate || 0,
    discount: 0, gst: 0, gst_rate: 0, total: 0,
  });
  const [previewLoading, setPreviewLoading] = useState(false);

  const setItem = (idx, patch) =>
    setItems((xs) => xs.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const subtotalLocal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0),
    [items]);

  const payload = () => ({
    items: items.map((it) => ({
      product_id: it.product_id || null,
      name: it.name || "",
      brand: it.brand || "",
      unit: it.unit || "MT",
      hsn: it.hsn || "",
      category: it.category || "",
      quantity: Number(it.quantity) || 0,
      unit_price: Number(it.unit_price) || 0,
    })),
    ...ship,
  });

  // Debounced live preview — fires whenever line items OR the admin's
  // Financial Rules change (the endpoint re-reads the settings doc each call).
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await api.post("/admin/buyer-quotations/preview-totals", {
          buyer_email: initial?.buyer_email || null,
          buyer_category: initial?.buyer_category || null,
          items: payload().items,
        });
        if (alive) setPreview(r.data);
      } catch { /* keep last preview */ }
      finally { if (alive) setPreviewLoading(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, initial?.buyer_email, initial?.buyer_category]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/buyer-quotations/${initial.id}`, payload());
      toast.success("Draft saved");
      onSaved && onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    // Basic validation
    if (items.some((it) => !it.name || !it.unit_price)) {
      toast.error("Fill in a name and rate on every line item");
      return;
    }
    if (!window.confirm(`Send this quotation to the buyer via ${rail === "phonepe" ? "PhonePe" : "Razorpay"}?\n\nIt will be locked for 48 hours; buyer receives an email + Accept & Pay link.`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/buyer-quotations/${initial.id}`, payload());
      const r = await api.post(`/admin/buyer-quotations/${initial.id}/send`, { payment_rail: rail });
      toast.success(`Sent · valid until ${new Date(r.data.valid_until).toLocaleString()}`);
      onSent && onSent(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-white/10 rounded-sm bg-black/30 p-4">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-3">Line items</div>
        {items.map((it, i) => (
          <div key={i} data-testid={`bq-line-${i}`} className="grid grid-cols-12 gap-2 items-center mb-2">
            <input data-testid={`bq-line-name-${i}`} placeholder="Item name"
                   value={it.name} onChange={(e) => setItem(i, { name: e.target.value })}
                   className="col-span-5 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            <input placeholder="Brand" value={it.brand} onChange={(e) => setItem(i, { brand: e.target.value })}
                   className="col-span-2 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            <input placeholder="Qty" type="number" value={it.quantity}
                   data-testid={`bq-line-qty-${i}`}
                   onChange={(e) => setItem(i, { quantity: e.target.value })}
                   className="col-span-1 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            <input placeholder="Unit" value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })}
                   className="col-span-1 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            <input placeholder="Rate ₹" type="number" value={it.unit_price}
                   data-testid={`bq-line-rate-${i}`}
                   onChange={(e) => setItem(i, { unit_price: e.target.value })}
                   className="col-span-1 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            <select value={it.category || ""} data-testid={`bq-line-cat-${i}`}
                    onChange={(e) => setItem(i, { category: e.target.value })}
                    className="col-span-1 bg-black/40 border border-white/15 rounded-sm px-1.5 py-1.5 text-[11px] text-[#F5F5F0] focus:outline-none focus:border-[#B87333]">
              <option value="">GST bucket…</option>
              <option value="cement">Cement</option>
              <option value="tmt">TMT / Steel</option>
              <option value="other">Other</option>
            </select>
            <button type="button" onClick={() => setItems(items.filter((_, ii) => ii !== i))}
                    className="col-span-1 text-[#8A8A82] hover:text-[#FF5A5F] flex justify-center" aria-label="Remove line">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button type="button" data-testid="bq-add-line"
                onClick={() => setItems([...items, { name: "", brand: "", unit: "MT", hsn: "", category: "", quantity: 1, unit_price: 0 }])}
                className="mt-2 text-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 hover:underline">
          <PlusCircle size={12} /> Add line item
        </button>

        {/* Live totals preview — reflects Admin → Financial Rules instantly */}
        <div data-testid="bq-live-totals" className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 gap-x-6 gap-y-1.5 font-body text-sm">
          <span className="text-[#8A8A82]">Subtotal</span>
          <span className="text-right text-[#F5F5F0] font-mono">₹{Number(preview.subtotal || subtotalLocal).toLocaleString("en-IN")}</span>
          <span className="text-[#8A8A82]">
            Tier discount
            <span className="ml-1 text-[10px] uppercase tracking-[0.16em] text-[#B87333]">
              ({Math.round((Number(preview.discount_rate) || 0) * 100)}%)
            </span>
          </span>
          <span data-testid="bq-tier-discount" className="text-right text-[#2FB999] font-mono">
            − ₹{Number(preview.discount || 0).toLocaleString("en-IN")}
          </span>
          <span className="text-[#8A8A82]">GST <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A82]">({Math.round((Number(preview.gst_rate) || 0) * 100)}%)</span></span>
          <span className="text-right text-[#F5F5F0] font-mono">₹{Number(preview.gst || 0).toLocaleString("en-IN")}</span>
          <span className="text-[#F5F5F0] font-serif">Grand Total</span>
          <span data-testid="bq-grand-total" className="text-right text-[#D8B23E] font-serif text-lg">
            ₹{Number(preview.total || 0).toLocaleString("en-IN")}
          </span>
          <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#6f6f68] text-right">
            {previewLoading ? "Recalculating…" : "Live · reflects Admin → Financial Rules"}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {[
          ["Contact name", "shipping_name"], ["Contact phone", "shipping_phone"],
          ["GSTIN", "gst_number"], ["Project name", "project_name"],
          ["Delivery state", "shipping_state"], ["Ship-to address", "shipping_address", true],
        ].map(([label, key, wide]) => (
          <div key={key} className={wide ? "md:col-span-2" : ""}>
            <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">{label}</label>
            <input value={ship[key] || ""} data-testid={`bq-${key}`}
                   onChange={(e) => setShip({ ...ship, [key]: e.target.value })}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          </div>
        ))}
      </div>

      <RailSelector testId="bq-rail" value={rail} onChange={setRail} label="Payment Rail · booking link sent to buyer" />

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={busy} data-testid="bq-save-draft"
                className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <FileText size={12}/>} Save draft
        </button>
        <button type="button" onClick={send} disabled={busy} data-testid="bq-send"
                className="bg-[#2FB999] hover:bg-[#28a086] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <Send size={12}/> Send quote (48h)
        </button>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/buyer-quotations/${initial.id}/preview.pdf`}
           target="_blank" rel="noreferrer" data-testid="bq-preview"
           className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5">
          Preview PDF <ExternalLink size={11}/>
        </a>
      </div>
    </div>
  );
}

/** Inline drawer used by QuoteRequests tab — auto-creates a draft on mount
 *  from the QR, then renders the editor. */
export function BuyerQuotationDraftDrawer({ api, qr, onDone }) {
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.post("/admin/buyer-quotations", { quote_request_id: qr.id });
        if (alive) setDraft(r.data);
      } catch (e) { if (alive) setErr(e.response?.data?.detail || "Could not create draft"); }
    })();
    return () => { alive = false; };
  }, [api, qr.id]);

  if (err) return <div data-testid="bq-drawer-error" className="mt-4 p-4 border border-[#FF5A5F]/40 bg-[#FF5A5F]/[0.06] rounded-sm text-[#FF5A5F] font-body text-sm">{err}</div>;
  if (!draft) return <div className="mt-4 p-4 font-body text-sm text-[#8A8A82]"><Loader2 size={13} className="inline animate-spin mr-2"/>Creating draft quotation…</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="mt-4 border border-[#B87333]/40 bg-[#B87333]/[0.05] rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">Draft quotation</div>
          <div className="font-serif text-lg text-[#F5F5F0]">{draft.quotation_number}</div>
        </div>
      </div>
      <BuyerQuotationEditor api={api} initial={draft} onSent={() => onDone && onDone()} onSaved={(d) => setDraft(d)} />
    </motion.div>
  );
}

/** Full Buyer Quotations admin tab. */
export function AdminBuyerQuotations({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/buyer-quotations")).data || []); }
    catch { toast.error("Could not load quotations"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const lanes = useMemo(() => {
    const buckets = { draft: [], sent: [], accepted: [], revision_requested: [], expired: [], cancelled: [] };
    for (const q of items) (buckets[q.status] || (buckets[q.status] = [])).push(q);
    return buckets;
  }, [items]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this quotation?")) return;
    try { await api.post(`/admin/buyer-quotations/${id}/cancel`); toast.success("Quotation cancelled"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  if (loading) return <div className="p-8 text-center font-body text-sm text-[#8A8A82]">Loading…</div>;

  const editing = editingId && items.find((x) => x.id === editingId);

  return (
    <div data-testid="admin-buyer-quotations" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl text-[#F5F5F0]">Buyer Quotations</h3>
        <button type="button" onClick={load} className="text-[#8A8A82] hover:text-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      <LetterheadOptions api={api} />

      <AnimatePresence>
        {editing && (
          <motion.div key={editing.id} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="border border-[#B87333]/40 bg-[#B87333]/[0.04] rounded-sm p-5 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">Editing</div>
                <div className="font-serif text-xl text-[#F5F5F0]">{editing.quotation_number} · {editing.buyer_email}</div>
              </div>
              <button onClick={() => setEditingId(null)} className="text-[#8A8A82] hover:text-[#FF5A5F]"><XCircle size={16}/></button>
            </div>
            <BuyerQuotationEditor api={api} initial={editing} onSent={() => { setEditingId(null); load(); }} onSaved={() => load()} />
          </motion.div>
        )}
      </AnimatePresence>

      {["draft", "sent", "revision_requested", "accepted", "expired", "cancelled"].map((lane) => (
        <div key={lane}>
          <div className="flex items-center gap-2 mb-2">
            <StatusPill status={lane}/>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">{lanes[lane]?.length || 0}</span>
          </div>
          {(lanes[lane] || []).length === 0 ? (
            <div className="font-body text-xs text-[#8A8A82] italic">Nothing here.</div>
          ) : (
            <ul className="space-y-2">
              {(lanes[lane] || []).map((q) => {
                const valid = q.valid_until ? new Date(q.valid_until) : null;
                const now = new Date();
                const hoursLeft = valid ? Math.max(0, Math.round((valid - now) / 3600000)) : null;
                return (
                  <li key={q.id} data-testid={`bq-row-${q.id}`} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg text-[#F5F5F0]">{q.quotation_number}</div>
                      <div className="font-body text-xs text-[#8A8A82]">
                        {q.buyer_email} · ₹{Number(q.total || 0).toLocaleString("en-IN")}
                        {q.rfq_ref ? ` · RFQ ${q.rfq_ref}` : ""}
                        {hoursLeft !== null && lane === "sent" ? ` · ${hoursLeft}h left` : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/buyer-quotations/${q.id}/preview.pdf`}
                         target="_blank" rel="noreferrer"
                         className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1">
                        PDF <ExternalLink size={10}/>
                      </a>
                      {(q.status === "draft" || q.status === "revision_requested") && (
                        <button data-testid={`bq-edit-${q.id}`} onClick={() => setEditingId(q.id)}
                                className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
                          {q.status === "revision_requested" ? "Revise & send" : "Open"} <FileText size={11}/>
                        </button>
                      )}
                      {q.status !== "accepted" && q.status !== "cancelled" && (
                        <button onClick={() => cancel(q.id)}
                                className="border border-white/20 hover:border-[#FF5A5F] hover:text-[#FF5A5F] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm text-[#C9C9C2] inline-flex items-center gap-1">
                          <XCircle size={11}/>
                        </button>
                      )}
                    </div>
                    {q.revision_note && lane === "revision_requested" && (
                      <div className="basis-full mt-2 text-xs text-[#B94FEB] font-body italic border-l-2 border-[#B94FEB]/40 pl-3">
                        <Clock size={10} className="inline mr-1"/>Buyer note: {q.revision_note}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
